import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import Seo from '../components/Seo';
import './Booking.css';

// Initialised once at module level — loadStripe defers the script load until
// Elements mounts, so there's no performance cost before the payment step.
const stripePromise = process.env.REACT_APP_STRIPE_PUBLIC_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY)
  : null;

/* ─── Config ─── */
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.stratosstandard.com';

const SERVICES = [
  { id: 'obj-foundational', label: 'Object Identification: Foundational', group: 'Object Identification', price: 129, duration: '45 min', icon: '🔵' },
  { id: 'obj-advanced',     label: 'Object Identification: Advanced',     group: 'Object Identification', price: 159, duration: '60 min', icon: '🟧', comingSoon: true },
  { id: 'obj-mastery',      label: 'Object Identification: Mastery',      group: 'Object Identification', price: 199, duration: '90 min', icon: '🌟', comingSoon: true },
  { id: 'math-foundational',label: 'Mathematics: Foundational',           group: 'Mathematics',           price: 129, duration: '45 min', icon: '🔢' },
  { id: 'math-advanced',    label: 'Mathematics: Advanced',               group: 'Mathematics',           price: 159, duration: '60 min', icon: '📐', comingSoon: true },
  { id: 'speech-narrative', label: 'Speech: Storytelling & Understanding', group: 'Speech & Language', price: 129, duration: '45 min', icon: '📖' },
  { id: 'speech-phonemic',  label: 'Speech: Sounds & Early Reading',       group: 'Speech & Language', price: 159, duration: '60 min', icon: '🗣️', comingSoon: true },
];

const MACH_SERVICES = [
  { id: 'mach-firstwords',       label: 'First Words',        icon: '💬', criterion: 'Says 3+ recognizable words used consistently' },
  { id: 'mach-firststeps',       label: 'First Steps',        icon: '👣', criterion: 'Takes 5+ unassisted steps in sequence' },
  { id: 'mach-colornaming',      label: 'Color Naming',       icon: '🎨', criterion: 'Names 5 colors correctly on sight' },
  { id: 'mach-shaperecognition', label: 'Shape Recognition',  icon: '🔷', criterion: 'Identifies circle, square, triangle, and star' },
  { id: 'mach-countstoten',      label: 'Counts to 10',       icon: '🔢', criterion: 'Counts 1 to 10 with no skips or repeats' },
  { id: 'mach-firstsentence',    label: 'First Sentence',     icon: '🗨️', criterion: 'Uses a 3+ word sentence to express a complete idea' },
];

const ADDONS = [
  { id: 'cover-mailing',  label: 'Certificate cover + mailing',  price: 50 },
  { id: 'framed-mailing', label: 'Framed certificate + mailing', price: 100 },
];

const MACH_BUNDLE_BASE = 70;
const MACH_BUNDLE_ADDITIONAL = 20;

function isMachId(id) {
  return typeof id === 'string' && id.startsWith('mach-');
}

function machBundlePrice(count) {
  if (count <= 0) return 0;
  return MACH_BUNDLE_BASE + MACH_BUNDLE_ADDITIONAL * (count - 1);
}

function computeTotal(data) {
  const addonObj = ADDONS.find(a => a.id === data.addon);
  const addonPrice = addonObj ? addonObj.price : 0;
  const machCount = (data.machCerts || []).length;
  if (machCount > 0) return machBundlePrice(machCount) + addonPrice;
  const svc = SERVICES.find(s => s.id === data.service);
  return (svc?.price || 0) + addonPrice;
}

// Mirror of lambda/discountUtils.computeDiscountAmount so the figure shown in
// the order summary always matches what bookingHandler verifies against Stripe.
// Whole-dollar rounding, clamped to [0, total].
function computeDiscountAmount(total, type, value) {
  if (!total || total <= 0) return 0;
  let off = type === 'percent' ? total * (value / 100) : (type === 'fixed' ? value : 0);
  off = Math.round(off);
  return Math.max(0, Math.min(off, total));
}

function selectedServiceLabel(data) {
  const machCount = (data.machCerts || []).length;
  if (machCount > 0) {
    if (machCount === 1) {
      const m = MACH_SERVICES.find(x => x.id === data.machCerts[0]);
      return m ? `MACH: ${m.label}` : 'MACH cert';
    }
    return `MACH Bundle (${machCount} certs)`;
  }
  const svc = SERVICES.find(s => s.id === data.service);
  return svc?.label || '';
}

const STEPS = ['Account', 'Service', 'Add-ons', 'Time Slot', 'Payment', 'Confirm'];


const MONTH_LABELS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW_LABELS   = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

function localDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

/* ─── Local mock slots (used as fallback when the API is unreachable) ─── */

// 15-minute platform slots from 8:00 AM to 5:45 PM (40 total)
const PLATFORM_SLOTS = (() => {
  const SLOT_START = 8  * 60;  // 8:00 AM
  const SLOT_END   = 18 * 60;  // 6:00 PM (exclusive)
  const slots = [];
  for (let m = SLOT_START; m < SLOT_END; m += 15) {
    const h        = Math.floor(m / 60);
    const min      = m % 60;
    const period   = h < 12 ? 'AM' : 'PM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const label    = min === 0
      ? `${displayH}:00 ${period}`
      : `${displayH}:${String(min).padStart(2, '0')} ${period}`;
    slots.push({ label, minutes: m });
  }
  return slots;
})();

/** Returns standard business-hour slots for weekdays, empty for weekends. */
function computeMockSlots(dateStr) {
  const dow = new Date(dateStr + 'T12:00:00').getDay();
  if (dow === 0 || dow === 6) return [];
  return PLATFORM_SLOTS
    .filter(s => s.minutes >= 9 * 60 && s.minutes < 17 * 60)
    .map(s => s.label);
}

/* ─── Sub-components ─── */

function StepIndicator({ current }) {
  return (
    <div className="step-indicator">
      {STEPS.map((label, i) => (
        <React.Fragment key={i}>
          <div className={`si-step ${i < current ? 'done' : ''} ${i === current ? 'active' : ''}`}>
            <div className="si-bubble">
              {i < current
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : i + 1}
            </div>
            <span className="si-label">{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`si-line ${i < current ? 'done' : ''}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

/* Step 0 – Account / Child Info */
function StepAccount({ data, onChange, onNext }) {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!data.parentFirst) e.parentFirst = 'Required';
    if (!data.parentLast)  e.parentLast  = 'Required';
    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) e.email = 'Valid email required';
    if (!data.phone) e.phone = 'Required';
    if (!data.childFirst) e.childFirst = 'Required';
    if (!data.childDob)   e.childDob   = 'Required';
    return e;
  };

  const handleNext = () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    onNext();
  };

  const field = (name, label, type = 'text', placeholder = '') => (
    <div className="form-group">
      <label className="form-label">{label}</label>
      <input
        type={type}
        className={`form-input ${errors[name] ? 'input-error' : ''}`}
        value={data[name] || ''}
        onChange={e => { onChange(name, e.target.value); if (errors[name]) setErrors(p => ({...p, [name]: null})); }}
        placeholder={placeholder}
      />
      {errors[name] && <span className="field-error">{errors[name]}</span>}
    </div>
  );

  return (
    <div className="booking-step">
      <h2 className="step-title-h2">Your Information</h2>
      <p className="step-sub">Tell us about you and your child so we can personalize the experience.</p>

      <div className="form-section-label">Parent / Guardian</div>
      <div className="form-row-2">
        {field('parentFirst', 'First Name', 'text', 'Jane')}
        {field('parentLast',  'Last Name',  'text', 'Smith')}
      </div>
      {field('email', 'Email Address', 'email', 'jane@example.com')}
      {field('phone', 'Phone Number', 'tel', '(555) 000-0000')}

      <div className="form-section-label" style={{ marginTop: '28px' }}>Child Information</div>
      <div className="form-row-2">
        {field('childFirst', 'Child\'s First Name', 'text', 'Lily')}
        {field('childLast',  'Child\'s Last Name',  'text', 'Smith')}
      </div>
      {field('childDob', 'Date of Birth', 'date')}

      <button className="btn btn-primary btn-lg step-next" onClick={handleNext}>
        Continue
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
      </button>
    </div>
  );
}

/* Step 1 – Service selection */
function StepService({ data, onChange, onNext, onBack }) {
  const machSelected = data.machCerts || [];
  const machCount    = machSelected.length;
  const machTotal    = machBundlePrice(machCount);
  const canContinue  = !!data.service || machCount > 0;

  const toggleMach = (id) => {
    const next = machSelected.includes(id)
      ? machSelected.filter(x => x !== id)
      : [...machSelected, id];
    onChange('machCerts', next);
  };

  return (
    <div className="booking-step">
      <h2 className="step-title-h2">Choose Your Service</h2>
      <p className="step-sub">
        Pick one or more MACH milestone certs to bundle, or choose a SKIES Foundational track below.
      </p>

      {/* MACH multi-select */}
      <div className="svc-group svc-group-mach">
        <p className="svc-group-label">
          MACH: Milestone Firsts
          <span className="svc-group-tag">Bundle pricing</span>
        </p>
        <p className="svc-group-sub">
          Select any combination. ${MACH_BUNDLE_BASE} for the first, +${MACH_BUNDLE_ADDITIONAL} for each additional cert in the same session.
        </p>
        <div className="service-selector">
          {MACH_SERVICES.map(m => {
            const isSelected = machSelected.includes(m.id);
            return (
              <label key={m.id} className={`svc-option ${isSelected ? 'selected' : ''}`}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggleMach(m.id)}
                />
                <div className="svc-opt-icon">{m.icon}</div>
                <div className="svc-opt-body">
                  <span className="svc-opt-name">{m.label}</span>
                  <span className="svc-opt-dur">{m.criterion}</span>
                </div>
                <div className="svc-opt-price">${MACH_BUNDLE_BASE}</div>
                {isSelected && (
                  <div className="svc-check">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                )}
              </label>
            );
          })}
        </div>
        {machCount > 0 && (
          <p className="mach-running-total">
            <span className="mach-running-label">
              {machCount} MACH cert{machCount > 1 ? 's' : ''} selected
            </span>
            <span className="mach-running-amt">${machTotal}</span>
          </p>
        )}
      </div>

      {/* SKIES Foundational radios — mutually exclusive with MACH */}
      <p className="svc-group-divider">or</p>

      {['Object Identification', 'Mathematics', 'Speech & Language'].map(group => (
        <div key={group} className="svc-group">
          <p className="svc-group-label">{group}</p>
          <div className="service-selector">
            {SERVICES.filter(s => s.group === group).map(svc => {
              const disabled = !!svc.comingSoon;
              return (
                <label
                  key={svc.id}
                  className={`svc-option ${data.service === svc.id ? 'selected' : ''} ${disabled ? 'svc-option-disabled' : ''}`}
                  aria-disabled={disabled || undefined}
                >
                  <input
                    type="radio"
                    name="service"
                    value={svc.id}
                    checked={data.service === svc.id}
                    onChange={() => onChange('service', svc.id)}
                    disabled={disabled}
                  />
                  <div className="svc-opt-icon">{svc.icon}</div>
                  <div className="svc-opt-body">
                    <span className="svc-opt-name">{svc.label}</span>
                    <span className="svc-opt-dur">{svc.duration}</span>
                  </div>
                  {disabled
                    ? <div className="svc-opt-cs-tag">Coming Soon</div>
                    : <div className="svc-opt-price">${svc.price}</div>}
                  {data.service === svc.id && !disabled && (
                    <div className="svc-check">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                  )}
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <div className="step-nav">
        <button className="btn btn-dark" onClick={onBack}>← Back</button>
        <button
          className="btn btn-primary btn-lg"
          onClick={onNext}
          disabled={!canContinue}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

/* Step 2 – Add-ons */
function StepAddons({ data, onChange, onNext, onBack }) {
  return (
    <div className="booking-step">
      <h2 className="step-title-h2">Optional Add-ons</h2>
      <p className="step-sub">Would you like a physical copy of your child's certificate mailed to you?</p>

      <div className="service-selector">
        <label className={`svc-option ${!data.addon ? 'selected' : ''}`}>
          <input
            type="radio"
            name="addon"
            checked={!data.addon}
            onChange={() => onChange('addon', null)}
          />
          <div className="svc-opt-icon">📄</div>
          <div className="svc-opt-body">
            <span className="svc-opt-name">Digital only</span>
            <span className="svc-opt-dur">Included with every service</span>
          </div>
          <div className="svc-opt-price">$0</div>
          {!data.addon && (
            <div className="svc-check">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          )}
        </label>

        {ADDONS.map(addon => (
          <label key={addon.id} className={`svc-option ${data.addon === addon.id ? 'selected' : ''}`}>
            <input
              type="radio"
              name="addon"
              value={addon.id}
              checked={data.addon === addon.id}
              onChange={() => onChange('addon', addon.id)}
            />
            <div className="svc-opt-icon">📬</div>
            <div className="svc-opt-body">
              <span className="svc-opt-name">{addon.label}</span>
              <span className="svc-opt-dur">Sent within 5 to 7 business days</span>
            </div>
            <div className="svc-opt-price">+${addon.price}</div>
            {data.addon === addon.id && (
              <div className="svc-check">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
            )}
          </label>
        ))}
      </div>

      <div className="step-nav">
        <button className="btn btn-dark" onClick={onBack}>← Back</button>
        <button className="btn btn-primary btn-lg" onClick={onNext}>
          Continue →
        </button>
      </div>
    </div>
  );
}

/* Calendar picker sub-component */
function CalendarPicker({ selectedDate, onSelect, availableDates, monthLoading, onMonthChange }) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayISO = localDateStr(today);

  const maxDate = new Date(today);
  maxDate.setDate(today.getDate() + 30);
  const maxISO = localDateStr(maxDate);

  const [viewYear,  setViewYear]  = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());

  useEffect(() => {
    onMonthChange(viewYear, viewMonth);
  }, [viewYear, viewMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const lastOfMonth  = new Date(viewYear, viewMonth + 1, 0);
  const startPad     = firstOfMonth.getDay();

  const canGoPrev = viewYear > today.getFullYear() || viewMonth > today.getMonth();
  const nextMonthFirst = new Date(viewYear, viewMonth + 1, 1);
  const canGoNext = localDateStr(nextMonthFirst) <= maxISO;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= lastOfMonth.getDate(); d++) {
    cells.push(new Date(viewYear, viewMonth, d));
  }

  return (
    <div className="cal-grid-wrap">
      <div className="cal-nav">
        <button className="cal-nav-btn" onClick={prevMonth} disabled={!canGoPrev}>&#8249;</button>
        <span className="cal-month-label">
          {MONTH_LABELS[viewMonth]} {viewYear}
          {monthLoading && <span className="cal-loading-dot" />}
        </span>
        <button className="cal-nav-btn" onClick={nextMonth} disabled={!canGoNext}>&#8250;</button>
      </div>

      <div className={`cal-grid${monthLoading ? ' cal-grid-loading' : ''}`}>
        {DOW_LABELS.map(dow => (
          <div key={dow} className="cal-weekday">{dow}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={`pad-${i}`} className="cal-cell-empty" />;
          const str = localDateStr(day);
          const isPast      = str < todayISO;
          const isTooFar    = str > maxISO;
          const noSlots     = !monthLoading && !availableDates.has(str);
          const isDisabled  = isPast || isTooFar || noSlots;
          const isSelected  = selectedDate === str;
          return (
            <button
              key={str}
              className={`cal-cell-date${isDisabled ? ' cal-disabled' : ''}${isSelected ? ' cal-selected' : ''}`}
              disabled={isDisabled}
              onClick={() => onSelect(str)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>

      <p className="cal-legend">
        <span className="cal-legend-dot cal-legend-disabled" /> No availability
        <span className="cal-legend-dot cal-legend-open" style={{ marginLeft: '16px' }} /> Available
      </p>
    </div>
  );
}

/* Step 3 – Time slot */
function StepSlot({ data, onChange, onNext, onBack }) {
  const [selectedDate, setSelectedDate] = useState(data.date || null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [availableDates, setAvailableDates] = useState(new Set());
  const [monthLoading, setMonthLoading] = useState(false);

  // The chosen service drives consultant-tier gating in the slots API: an
  // Advanced or Mastery service hides slots from consultants below the
  // matching SKIES credential rank. MACH milestone certs are basic-tier so
  // we skip the gating query for them.
  const serviceQuery = data.service && !isMachId(data.service)
    ? `&service=${encodeURIComponent(data.service)}`
    : '';

  // Fetch availability for every in-range date in the viewed month in parallel so
  // the calendar can grey out days with no open slots without a per-click delay.
  // Only dates between today and the 30-day booking window are fetched.
  const fetchMonthAvailability = async (year, month) => {
    setMonthLoading(true);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayISO = localDateStr(today);
    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + 30);
    const maxISO = localDateStr(maxDate);

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const datesToFetch = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const str = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      if (str >= todayISO && str <= maxISO) datesToFetch.push(str);
    }

    // Fire all slot queries concurrently; fall back to mock data per date if the
    // API is unreachable so the calendar stays functional in development.
    const results = await Promise.all(
      datesToFetch.map(async str => {
        try {
          const res = await fetch(`${API_BASE}/slots?date=${str}${serviceQuery}`);
          if (!res.ok) throw new Error('not ok');
          const json = await res.json();
          return { str, hasSlots: (json.available || []).length > 0 };
        } catch {
          return { str, hasSlots: computeMockSlots(str).length > 0 };
        }
      })
    );

    setAvailableDates(new Set(results.filter(r => r.hasSlots).map(r => r.str)));
    setMonthLoading(false);
  };

  const fetchSlots = async (dateStr) => {
    setSlotsLoading(true);
    setSlotsError(null);
    setSlots([]);
    try {
      const res = await fetch(`${API_BASE}/slots?date=${dateStr}${serviceQuery}`);
      if (!res.ok) throw new Error('not ok');
      const json = await res.json();
      setSlots(json.available || []);
    } catch {
      // API unreachable — fall back to local mock consultant data
      setSlots(computeMockSlots(dateStr));
    } finally {
      setSlotsLoading(false);
    }
  };

  const handleDateSelect = (dateStr) => {
    setSelectedDate(dateStr);
    onChange('date', dateStr);
    onChange('time', null); // clear any previously chosen time when the date changes
    fetchSlots(dateStr);
  };

  return (
    <div className="booking-step">
      <h2 className="step-title-h2">Select a Time Slot</h2>
      <p className="step-sub">Pick a date and time that works for you and your little one. Availability shown up to 30 days ahead.</p>

      <CalendarPicker
        selectedDate={selectedDate}
        onSelect={handleDateSelect}
        availableDates={availableDates}
        monthLoading={monthLoading}
        onMonthChange={fetchMonthAvailability}
      />

      {selectedDate && (
        <div className="slots-grid">
          {slotsLoading
            ? <p className="no-slots">Loading available times…</p>
            : slotsError
              ? <p className="no-slots" style={{ color: '#c0392b' }}>{slotsError}</p>
              : slots.length === 0
                ? <p className="no-slots">No slots available on this day. Please try another date.</p>
                : slots.map(t => (
                  <button
                    key={t}
                    className={`time-slot ${data.time === t ? 'selected' : ''}`}
                    onClick={() => onChange('time', t)}
                  >
                    {t}
                  </button>
                ))
          }
        </div>
      )}

      <div className="step-nav">
        <button className="btn btn-dark" onClick={onBack}>← Back</button>
        <button
          className="btn btn-primary btn-lg"
          onClick={onNext}
          disabled={!data.date || !data.time}
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

/* Step 3 – Payment
 *
 * Architecture note: useStripe() and useElements() MUST be called inside a
 * component that is a descendant of <Elements>.  StepPayment is the outer shell
 * that owns the order summary and decides between demo mode and live mode.
 * StripeCardForm is the inner component — rendered only inside <Elements> — that
 * safely calls the Stripe hooks and handles card confirmation.
 */

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      color: '#0a1628',
      '::placeholder': { color: '#94a3b8' },
    },
    invalid: { color: '#dc2626' },
  },
};

/** Discount-code entry shown in the order summary on the payment step.
 *  Applying a valid code lifts it into booking state; the order total and the
 *  PaymentIntent both update to the discounted amount. Invalid codes surface an
 *  inline message and leave the price untouched. */
function DiscountField({ baseTotal, discount, onApply, onRemove }) {
  const [code,    setCode]    = useState('');
  const [status,  setStatus]  = useState('idle');   // idle | checking | error
  const [message, setMessage] = useState(null);

  const apply = async () => {
    const trimmed = code.trim();
    if (!trimmed) { setStatus('error'); setMessage('Enter a code first.'); return; }
    setStatus('checking');
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/discount/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed, amount: baseTotal }),
      });
      const json = await res.json().catch(() => ({}));
      if (res.ok && json.valid) {
        onApply({ code: json.code, discountType: json.discountType, discountValue: json.discountValue });
        setStatus('idle');
        setMessage(null);
        setCode('');
      } else {
        setStatus('error');
        setMessage(json.error || 'This code is invalid.');
      }
    } catch {
      setStatus('error');
      setMessage('Could not check the code. Please try again.');
    }
  };

  if (discount) {
    const off = computeDiscountAmount(baseTotal, discount.discountType, discount.discountValue);
    return (
      <div className="discount-applied">
        <span className="discount-applied-info">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          Code <strong>{discount.code}</strong> applied (${off} off)
        </span>
        <button type="button" className="discount-remove" onClick={onRemove}>Remove</button>
      </div>
    );
  }

  return (
    <div className="discount-field">
      <label className="discount-label">Have a discount code?</label>
      <div className="discount-input-row">
        <input
          type="text"
          className={`discount-input${status === 'error' ? ' discount-input-error' : ''}`}
          value={code}
          onChange={e => {
            setCode(e.target.value.toUpperCase());
            if (status === 'error') { setStatus('idle'); setMessage(null); }
          }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); apply(); } }}
          placeholder="Enter code"
          disabled={status === 'checking'}
          autoComplete="off"
          aria-label="Discount code"
        />
        <button
          type="button"
          className="btn btn-aero discount-apply-btn"
          onClick={apply}
          disabled={status === 'checking'}
        >
          {status === 'checking' ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {status === 'error' && message && (
        <span className="discount-message discount-message-error" role="alert">{message}</span>
      )}
    </div>
  );
}

/** Inner form — must be rendered inside <Elements>. */
function StripeCardForm({ data, onChange, onNext, onBack, total }) {
  const stripe   = useStripe();
  const elements = useElements();

  const [paying,       setPaying]      = useState(false);
  const [cardError,    setCardError]   = useState(null);
  const [intentReady,  setIntentReady] = useState(false);
  const [intentError,  setIntentError] = useState(null);

  const description = `Stratos Standard: ${selectedServiceLabel(data)}`;

  // Create a PaymentIntent as soon as the card form mounts so Stripe can begin
  // fraud analysis before the user clicks Pay.  The intent is keyed to the exact
  // charge amount: an existing secret is reused only when it was created for this
  // same total (e.g. navigating back to this step), but applying or removing a
  // discount changes `total` and forces a fresh intent so Stripe charges the
  // right amount and bookingHandler's amount-match check passes.
  useEffect(() => {
    let cancelled = false;
    if (data.clientSecret && data.clientSecretAmount === total) {
      setIntentReady(true);
      return;
    }
    setIntentReady(false);
    setIntentError(null);
    fetch(`${API_BASE}/payment-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        amount: total * 100, // Stripe requires the amount in the smallest currency unit (cents)
        description,
      }),
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(({ clientSecret }) => {
        if (cancelled) return;
        onChange('clientSecret', clientSecret);
        onChange('clientSecretAmount', total);
        setIntentReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setIntentError('Could not initialise payment session. Please refresh and try again.');
        setIntentReady(true);
      });
    return () => { cancelled = true; };
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePay = async () => {
    setCardError(null);
    setPaying(true);
    const card = elements.getElement(CardElement);
    const { error, paymentIntent } = await stripe.confirmCardPayment(data.clientSecret, {
      payment_method: { card },
    });
    setPaying(false);
    if (error) { setCardError(error.message); return; }
    onChange('paymentIntentId', paymentIntent.id);
    onNext();
  };

  return (
    <>
      <div className="payment-form">
        <div className="form-group">
          <label className="form-label">Card details</label>
          <div className={`stripe-card-element${cardError ? ' stripe-card-error' : ''}`}>
            <CardElement options={CARD_ELEMENT_OPTIONS} onChange={() => setCardError(null)} />
          </div>
          {cardError   && <span className="field-error">{cardError}</span>}
          {intentError && <span className="field-error">{intentError}</span>}
        </div>
        <div className="payment-secure-note">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>
          Encrypted and processed securely by Stripe. Your card details never touch our servers.
        </div>
      </div>
      <div className="step-nav">
        <button className="btn btn-dark" onClick={onBack} disabled={paying}>← Back</button>
        <button className="btn btn-primary btn-lg" onClick={handlePay} disabled={paying || !intentReady || !stripe || !data.clientSecret}>
          {paying ? <span className="spinner" /> : null}
          {paying ? 'Processing…' : `Pay $${total}`}
        </button>
      </div>
    </>
  );
}

/** Outer shell — handles order summary, demo mode, and the Elements provider. */
function StepPayment({ data, onChange, onNext, onBack }) {
  const addonObj  = ADDONS.find(a => a.id === data.addon) || null;
  const total     = computeTotal(data);
  const discount       = data.discount || null;
  const discountAmount = discount ? computeDiscountAmount(total, discount.discountType, discount.discountValue) : 0;
  const effectiveTotal = total - discountAmount;
  const machList  = (data.machCerts || []).map(id => MACH_SERVICES.find(m => m.id === id)).filter(Boolean);
  const isMachBundle = machList.length > 0;
  const svc       = !isMachBundle ? (SERVICES.find(s => s.id === data.service) || {}) : null;

  const handleDemoContinue = () => {
    onChange('paymentIntentId', 'DEMO-' + Date.now());
    onNext();
  };

  return (
    <div className="booking-step">
      <h2 className="step-title-h2">Payment</h2>
      <p className="step-sub">
        {stripePromise
          ? 'Your card is charged now. The booking is confirmed on the next step.'
          : 'Running in demo mode. No real payment will be taken.'}
      </p>

      <div className="order-summary">
        {isMachBundle ? (
          <>
            <div className="os-row">
              <span>Service</span>
              <span>MACH Bundle ({machList.length} cert{machList.length > 1 ? 's' : ''})</span>
            </div>
            {machList.map((m, i) => (
              <div key={m.id} className="os-row os-row-sub">
                <span>· {m.label}</span>
                <span>{i === 0 ? `$${MACH_BUNDLE_BASE}` : `+$${MACH_BUNDLE_ADDITIONAL}`}</span>
              </div>
            ))}
          </>
        ) : (
          <div className="os-row"><span>Service</span><span>{svc.label}</span></div>
        )}
        {addonObj && <div className="os-row"><span>Add-on</span><span>{addonObj.label}</span></div>}
        <div className="os-row"><span>Date</span><span>{data.date}</span></div>
        <div className="os-row"><span>Time</span><span>{data.time}</span></div>
        <div className="os-divider" />
        {discountAmount > 0 ? (
          <>
            <div className="os-row"><span>Subtotal</span><span>${total}</span></div>
            <div className="os-row os-discount">
              <span>Discount ({discount.code})</span><span>−${discountAmount}</span>
            </div>
            <div className="os-row os-total"><span>Total</span><span>${effectiveTotal}</span></div>
          </>
        ) : (
          <div className="os-row os-total"><span>Total</span><span>${total}</span></div>
        )}

        <DiscountField
          baseTotal={total}
          discount={discount}
          onApply={d => onChange('discount', d)}
          onRemove={() => onChange('discount', null)}
        />
      </div>

      {stripePromise ? (
        <Elements stripe={stripePromise}>
          <StripeCardForm data={data} onChange={onChange} onNext={onNext} onBack={onBack} total={effectiveTotal} />
        </Elements>
      ) : (
        <>
          <div className="payment-demo-notice">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <div>
              <strong>Demo mode</strong>
              <p>Set <code>REACT_APP_STRIPE_PUBLIC_KEY</code> to enable live payments.</p>
            </div>
          </div>
          <div className="step-nav">
            <button className="btn btn-dark" onClick={onBack}>← Back</button>
            <button className="btn btn-primary btn-lg" onClick={handleDemoContinue}>Continue →</button>
          </div>
        </>
      )}
    </div>
  );
}

/* Step 5 – Review & Confirm */
function StepReview({ data, onBack, onSubmit, loading, submitError }) {
  const addonObj = ADDONS.find(a => a.id === data.addon) || null;
  const total = computeTotal(data);
  const discount       = data.discount || null;
  const discountAmount = discount ? computeDiscountAmount(total, discount.discountType, discount.discountValue) : 0;
  const effectiveTotal = total - discountAmount;
  const machList = (data.machCerts || []).map(id => MACH_SERVICES.find(m => m.id === id)).filter(Boolean);
  const isMachBundle = machList.length > 0;
  const svc = !isMachBundle ? (SERVICES.find(s => s.id === data.service) || {}) : null;

  return (
    <div className="booking-step">
      <h2 className="step-title-h2">Review Your Booking</h2>
      <p className="step-sub">Please confirm all details before finalizing.</p>

      <div className="review-card">
        <div className="review-section">
          <p className="review-label">Parent / Guardian</p>
          <p>{data.parentFirst} {data.parentLast}</p>
          <p>{data.email}</p>
          <p>{data.phone}</p>
        </div>
        <div className="review-section">
          <p className="review-label">Child</p>
          <p>{data.childFirst} {data.childLast}</p>
          <p>DOB: {data.childDob}</p>
        </div>
        <div className="review-section">
          <p className="review-label">Session</p>
          {isMachBundle ? (
            <>
              <p><strong>MACH Bundle ({machList.length} cert{machList.length > 1 ? 's' : ''})</strong></p>
              {machList.map((m, i) => (
                <p key={m.id}>
                  {m.icon} {m.label}
                  {': '}
                  {i === 0 ? `$${MACH_BUNDLE_BASE}` : `+$${MACH_BUNDLE_ADDITIONAL}`}
                </p>
              ))}
            </>
          ) : (
            <p>{svc.icon} {svc.label}: ${svc.price}</p>
          )}
          {addonObj && <p>+ {addonObj.label}: ${addonObj.price}</p>}
          {discountAmount > 0 && <p>Discount ({discount.code}): −${discountAmount}</p>}
          <p><strong>Total: ${effectiveTotal}</strong></p>
          <p>{data.date} at {data.time}</p>
          <p>Virtual session</p>
        </div>
      </div>

      <p className="review-consent">
        By clicking "Confirm &amp; Book" you agree to our{' '}
        <a href="/contact" style={{ color: 'var(--aero)' }}>Terms of Service</a> and{' '}
        <a href="/contact" style={{ color: 'var(--aero)' }}>Privacy Policy</a>.
        A confirmation email will be sent to <strong>{data.email}</strong>.
      </p>

      {submitError && (
        <p className="form-error" role="alert" style={{ marginTop: 16 }}>
          {submitError} If your card was charged, your payment is captured. Contact{' '}
          <a href="mailto:hello@stratosstandard.com">hello@stratosstandard.com</a>{' '}
          with this page open and we will recover your booking.
        </p>
      )}

      <div className="step-nav">
        <button className="btn btn-dark" onClick={onBack} disabled={loading}>← Back</button>
        <button className="btn btn-primary btn-lg" onClick={onSubmit} disabled={loading}>
          {loading ? <span className="spinner" /> : null}
          {loading ? 'Booking…' : 'Confirm & Book'}
        </button>
      </div>
    </div>
  );
}

/* ─── Main Booking page ─── */
export default function Booking() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [data, setData] = useState(() => {
    const param = searchParams.get('service');
    if (param && isMachId(param)) return { service: null, machCerts: [param] };
    return { service: param || null, machCerts: [] };
  });

  // Enforce mutual exclusion between MACH multi-select and single foundational
  // service: picking one clears the other so the order summary never disagrees
  // with the user's last action.
  const set = (key, val) => setData(prev => {
    const next = { ...prev, [key]: val };
    if (key === 'service' && val) next.machCerts = [];
    if (key === 'machCerts' && Array.isArray(val) && val.length > 0) next.service = null;
    return next;
  });

  const submitBooking = async () => {
    setLoading(true);
    setSubmitError(null);
    try {
      const machCerts = data.machCerts || [];
      // For MACH bundles we send a synthetic single-string `service` so the
      // existing API contract still has a primitive value, plus the array of
      // selected MACH IDs in `machCerts` for the backend to itemize. A
      // single-cert MACH booking is sent as just its MACH ID so deep-linked
      // single-cert flows stay round-trippable.
      const machServiceField = machCerts.length === 1
        ? machCerts[0]
        : `mach-bundle:${machCerts.join('+')}`;
      const payload = {
        parentName:      `${data.parentFirst} ${data.parentLast}`,
        parentEmail:     data.email,
        parentPhone:     data.phone,
        childName:       `${data.childFirst} ${data.childLast}`,
        childDob:        data.childDob,
        service:         machCerts.length > 0 ? machServiceField : data.service,
        machCerts:       machCerts.length > 0 ? machCerts : undefined,
        addon:           data.addon || null,
        date:            data.date,
        time:            data.time,
        mode:            data.mode || 'virtual',
        paymentIntentId: data.paymentIntentId,
        // Sent only when a code was applied; bookingHandler re-validates and
        // recomputes the charge server-side regardless of what's sent here.
        discountCode:    data.discount?.code || undefined,
      };

      const res = await fetch(`${API_BASE}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let detail = 'Booking failed';
        try {
          const errBody = await res.json();
          detail = errBody.error || detail;
        } catch { /* non-JSON error body */ }
        throw new Error(detail);
      }
      const result = await res.json();
      navigate('/book/success', { state: { bookingId: result.bookingId, ...data } });
    } catch (err) {
      // Never fall through to a fake-success page — the parent has paid Stripe
      // but no booking exists, which silently orphans charges (audit H-5).
      setSubmitError(err.message || 'Booking failed. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Book a Verification Session"
        description="Book a warm, play-based verification session for your child ages 0–6. Choose an achievement, pick a time with a SKIES consultant, and earn a permanent Stratos Registry entry."
        path="/book"
      />
      <section className="page-hero" style={{ padding: '100px 0 48px' }}>
        <div className="container">
          <p className="eyebrow" style={{ color: '#3ba3e8', position: 'relative' }}>Book a Session</p>
          <h1 className="page-hero-title">Schedule Your SKIES Session</h1>
          <p className="page-hero-sub">Just a few steps to get your child's achievement officially recognized.</p>
        </div>
      </section>

      <section className="booking-section">
        <div className="container">
          <StepIndicator current={step} />

          <div className="booking-layout">
            <div className="booking-main">
              {step === 0 && <StepAccount data={data} onChange={set} onNext={() => setStep(1)} />}
              {step === 1 && <StepService data={data} onChange={set} onNext={() => setStep(2)} onBack={() => setStep(0)} />}
              {step === 2 && <StepAddons  data={data} onChange={set} onNext={() => setStep(3)} onBack={() => setStep(1)} />}
              {step === 3 && <StepSlot    data={data} onChange={set} onNext={() => setStep(4)} onBack={() => setStep(2)} />}
              {step === 4 && <StepPayment data={data} onChange={set} onNext={() => setStep(5)} onBack={() => setStep(3)} />}
              {step === 5 && <StepReview  data={data} onBack={() => setStep(4)} onSubmit={submitBooking} loading={loading} submitError={submitError} />}
            </div>

            {/* Sidebar */}
            <div className="booking-sidebar">
              <div className="sidebar-card">
                <h3>Why Choose Us</h3>
                {['Certified SKIES consultants','Play-based, stress-free sessions','Official certificate issued quickly','Trusted by 500+ families'].map((item, i) => (
                  <div key={i} className="sidebar-item">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d4a017" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    {item}
                  </div>
                ))}
              </div>

              {(data.machCerts && data.machCerts.length > 0) ? (
                <div className="sidebar-card sidebar-selection">
                  <p className="sidebar-card-label">Selected MACH Bundle</p>
                  {data.machCerts
                    .map(id => MACH_SERVICES.find(m => m.id === id))
                    .filter(Boolean)
                    .map((m, i) => (
                      <div key={m.id} className="sel-svc">
                        <span className="sel-icon">{m.icon}</span>
                        <div>
                          <p className="sel-name">{m.label}</p>
                          <p className="sel-age">{m.criterion}</p>
                        </div>
                        <span className="sel-price">
                          {i === 0 ? `$${MACH_BUNDLE_BASE}` : `+$${MACH_BUNDLE_ADDITIONAL}`}
                        </span>
                      </div>
                    ))}
                  <div className="sel-total-row">
                    <span>Bundle total</span>
                    <strong>${machBundlePrice(data.machCerts.length)}</strong>
                  </div>
                </div>
              ) : data.service && (() => {
                const svc = SERVICES.find(s => s.id === data.service);
                return svc ? (
                  <div className="sidebar-card sidebar-selection">
                    <p className="sidebar-card-label">Selected Service</p>
                    <div className="sel-svc">
                      <span className="sel-icon">{svc.icon}</span>
                      <div>
                        <p className="sel-name">{svc.label}</p>
                        <p className="sel-age">{svc.sub}</p>
                      </div>
                      <span className="sel-price">${svc.price}</span>
                    </div>
                  </div>
                ) : null;
              })()}

              <div className="sidebar-card sidebar-help">
                <h4>Need Help?</h4>
                <p>Our team is happy to answer any questions before you book.</p>
                <a href="mailto:hello@stratosstandard.com" className="btn btn-aero btn-sm" style={{ marginTop: '12px', width: '100%', justifyContent: 'center' }}>
                  Contact Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
